import React, { useEffect, useState } from "react"
import { Card, CardBody, CardTitle, Button } from "reactstrap"
import { Formik, Form, Field, ErrorMessage } from "lib/formik"
import Breadcrumbs from "components/Common/Breadcrumb"
import { useLocation, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import Spinners from "components/Shared/Spinners"
import { showAlert } from "store/reducers/alertReducer"
import BackButton from "components/Shared/BackButton"

import Select from "components/Shared/TranslatedSelect"
import { allOption } from "components/Common/constants"
import {
  addItemGroup,
  getItemGroupDetails,
  updateItemGroup,
} from "store/actions/itemGroupsAction"
import { addItemGroupSchema } from "components/Common/validation"
import { getItemsListDropdown } from "store/actions/itemsAction"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
import { getCountryList } from "store/actions/countriesAction"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"

const initialValues = {
  name: "",
  nameAr: "",
  minimum: 1,
  maximum: 1,
  quantity: 1,
  items: [],
  countryCode: null,
}

const AddItemGroup = ({ mode }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${mode === "add" ? t("Add") : t("Edit")} ${t(
    "Item Group "
  )}`

  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const dispatch = useDispatch()
  const { loading, isLoading, itemGroupDetails } = useSelector(
    state => state.ItemGroups
  )
  const { itemsListDropdown } = useSelector(state => state.Items)
  const { countryList } = useSelector(state => state.Countries)

  const [formValues, setFormValues] = useState(initialValues)
  const [isAllSelected, setIsAllSelected] = useState(false)

  const { userRole, accountType } = useSelector(state => state.Login)

  useEffect(() => {
    const menusSubPermissions = getSubPermissions(
      userRole,
      4,
      "menus_1",
      accountType
    )
    const hasComboAccess =
      menusSubPermissions?.combo_access_view?.isEnabled === true
    const isPermissionCreate = isPermissionEnabled(
      userRole,
      4,
      "menus_2",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      4,
      "menus_3",
      accountType
    )
    if (!hasComboAccess) {
      navigate("/dashboard")
      return
    }
    if (mode === "add") {
      if (!isPermissionCreate) {
        navigate("/dashboard")
      }
    } else if (mode === "edit") {
      if (!isPermissionEdit) {
        navigate("/dashboard")
      }
    }
  }, [])

  useEffect(() => {
    // getAllItemsList()
    fetchCountryList()
    mode === "edit" && getOldValues()
  }, [])

  const getOldValues = () => {
    const payload = {
      id,
    }
    dispatch(getItemGroupDetails(payload))
  }

  const fetchCountryList = () => {
    dispatch(getCountryList())
  }

  useEffect(() => {
    if (itemGroupDetails && mode === "edit") {
      const {
        itemType,
        type,
        name,
        nameAr,
        minimum,
        maximum,
        quantity,
        items,
        countryCode,
      } = itemGroupDetails

      getAllItemsList(countryCode)

      const itemsOptions = items?.map(item => ({
        label: language === "sa" ? item.nameAr : item.name,
        value: item._id,
      }))

      const matchedCountry = countryList.find(
        item => item.countryCode === countryCode
      )
      var updatedCountryCode = null
      if (matchedCountry) {
        const { name, nameAr, countryCode } = matchedCountry
        updatedCountryCode = {
          label: language === "sa" ? nameAr : name,
          value: countryCode,
        }
      }

      setFormValues(prevVal => ({
        ...prevVal,
        name,
        nameAr,
        minimum,
        maximum,
        quantity,
        items: itemsOptions,
        countryCode: updatedCountryCode,
      }))
    }
  }, [itemGroupDetails, countryList])

  const getAllItemsList = countryCode => {
    const payload = {
      category: [],
      countryCode,
    }
    dispatch(getItemsListDropdown(payload))
  }

  const handleSubmit = values => {
    const { name, nameAr, minimum, maximum, quantity, items, countryCode } =
      values

    const updatedItems = items?.map(item => item.value)

    const payload = {
      name,
      nameAr,
      minimum,
      maximum,
      quantity,
      items: updatedItems,
      countryCode: countryCode?.value?.toUpperCase(),
    }
    if (mode === "add") {
      dispatch(addItemGroup(payload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate("/menus/item-groups")
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    } else if (mode === "edit") {
      const editPayload = {
        ...payload,
        id: id,
      }
      dispatch(updateItemGroup(editPayload))
        .then(action => {
          const { statusCode, status, msg } = action?.payload
          if (statusCode === 200 && status) {
            dispatch(showAlert({ text: msg, type: "success" }))
            navigate(-1)
          } else {
            dispatch(showAlert({ text: msg, type: "error" }))
          }
        })
        .catch(error => {})
    }
  }

  const itemGroupsListOptions = itemsListDropdown?.map(item => ({
    label: language === "sa" ? item.nameAr : item.name,
    value: item._id,
  }))

  const options = isAllSelected
    ? itemGroupsListOptions
    : [allOption, ...itemGroupsListOptions]

  if (mode === "edit" && loading) return <Spinners />

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Card>
          <CardBody>
            <CardTitle className="d-flex align-items-center">
              <BackButton />
              {mode === "add" ? t("Add") : t("Edit")} {t("Item Group")}
            </CardTitle>
            <Formik
              enableReinitialize={true}
              initialValues={formValues}
              onSubmit={handleSubmit}
              validationSchema={addItemGroupSchema()}
            >
              {({ errors, touched, values, isSubmitting, setFieldValue }) => (
                <Form>
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="name">
                          {" "}
                          <span className="text-danger">*</span>{" "}
                          {t("Name(English)")}
                        </label>
                        <Field
                          type="text"
                          id="name"
                          name="name"
                          placeholder={t("Name(English)")}
                          className={`form-control ${
                            touched.name && errors.name ? "is-invalid" : ""
                          }`}
                        />
                        <TextError name="name" />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="nameAr">
                          <span className="text-danger">*</span>{" "}
                          {t("Name(عربي)")}
                        </label>
                        <Field
                          type="text"
                          style={{ direction: "rtl" }}
                          id="nameAr"
                          name="nameAr"
                          placeholder={t("Name(عربي)")}
                          className={`form-control ${
                            touched.nameAr && errors.nameAr ? "is-invalid" : ""
                          }`}
                        />
                        <TextError name="nameAr" />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="mt-2">
                        <label htmlFor="countryCode">
                          <span className="text-danger">*</span>{" "}
                          {t("Select Country")}
                        </label>
                        <Select
                          value={values.countryCode}
                          onChange={selectedValue => {
                            setFieldValue("countryCode", selectedValue)
                            getAllItemsList(selectedValue.value)
                            setFieldValue("items", "")
                          }}
                          options={countryList?.map(item => ({
                            label: language === "sa" ? item.nameAr : item.name,
                            value: item.countryCode,
                          }))}
                          placeholder={t("Select Country")}
                          className={`select2-selection ${
                            touched.countryCode && errors.countryCode
                              ? "danger_color_border"
                              : ""
                          } `}
                        />
                        <TextError name="countryCode" />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="minimum">
                          <span className="text-danger">*</span> {t("Minimum")}
                        </label>
                        <Field
                          type="text"
                          id="minimum"
                          name="minimum"
                          placeholder="e.g. 1"
                          className={`form-control ${
                            touched.minimum && errors.minimum
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="minimum" />
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="maximum">
                          <span className="text-danger">*</span> {t("Maximum")}
                        </label>
                        <Field
                          type="text"
                          id="maximum"
                          name="maximum"
                          placeholder="e.g. 1"
                          className={`form-control ${
                            touched.maximum && errors.maximum
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="maximum" />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="form_field">
                        <label htmlFor="quantity">
                          <span className="text-danger">*</span> {t("Quantity")}
                        </label>
                        <Field
                          type="number"
                          id="quantity"
                          name="quantity"
                          placeholder="e.g. 1"
                          className={`form-control ${
                            touched.quantity && errors.quantity
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <TextError name="quantity" />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="mt-2">
                        <label htmlFor="items">
                          <span className="text-danger">*</span>{" "}
                          {t("Select Items")}
                        </label>
                        <Select
                          value={values.items}
                          isMulti={true}
                          onChange={item => {
                            if (item?.find(option => option.value === "*")) {
                              setFieldValue("items", itemGroupsListOptions)
                              setIsAllSelected(true)
                            } else {
                              setFieldValue("items", item)
                              setIsAllSelected(
                                item.length === itemsListDropdown.length
                              )
                            }
                          }}
                          options={options}
                          className={`select2-selection ${
                            touched.items && errors.items
                              ? "danger_color_border"
                              : ""
                          } `}
                          closeMenuOnSelect={false}
                          placeholder={t("Select Items")}
                        />
                        <TextError name="items" />
                      </div>
                    </div>

                    <div className="mt-5"></div>
                    <div className="col-12 d-flex justify-content-end mb-5">
                      <Button
                        type="button"
                        className="mx-2"
                        color="secondary"
                        onClick={() => navigate(-1)}
                      >
                        {t("Cancel")}
                      </Button>
                      <Button
                        type="submit"
                        color="success"
                        disabled={isLoading}
                        className="w_fit_content"
                      >
                        {mode === "add" ? t("Add") : t("Save")}
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default AddItemGroup
